import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook para gerenciar scaling de canvas em alta resolução (HiDPI/Retina)
 * 
 * @param {Object} options - Opções de configuração
 * @param {number} options.width - Largura desejada do canvas em CSS pixels
 * @param {number} options.height - Altura desejada do canvas em CSS pixels
 * @param {boolean} options.autoResize - Se deve redimensionar automaticamente (padrão: true)
 * @returns {Object} - Objeto com canvasRef, context, devicePixelRatio e funções utilitárias
 */
const useCanvasScaling = ({ width, height, autoResize = true } = {}) => {
  const canvasRef = useRef(null);
  const devicePixelRatioRef = useRef(window.devicePixelRatio || 1);
  const contextRef = useRef(null);

  // Função para configurar o canvas com scaling correto
  const setupCanvas = useCallback((canvas, desiredWidth, desiredHeight) => {
    if (!canvas) return null;

    const dpr = window.devicePixelRatio || 1;
    devicePixelRatioRef.current = dpr;

    // Configurar tamanho do canvas em CSS pixels
    canvas.style.width = `${desiredWidth}px`;
    canvas.style.height = `${desiredHeight}px`;

    // Configurar tamanho interno do canvas (considerando devicePixelRatio)
    canvas.width = desiredWidth * dpr;
    canvas.height = desiredHeight * dpr;

    // Obter contexto 2D
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Escalar o contexto para compensar o devicePixelRatio
    ctx.scale(dpr, dpr);

    // Configurar propriedades de renderização para melhor qualidade
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    contextRef.current = ctx;
    return ctx;
  }, []);

  // Função para redimensionar o canvas
  const resizeCanvas = useCallback((newWidth, newHeight) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    return setupCanvas(canvas, newWidth, newHeight);
  }, [setupCanvas]);

  // Função para obter coordenadas do mouse ajustadas para o scaling
  const getMousePosition = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const dpr = devicePixelRatioRef.current;

    return {
      x: (event.clientX - rect.left) * dpr / dpr, // Já está em CSS pixels devido ao scale
      y: (event.clientY - rect.top) * dpr / dpr   // Já está em CSS pixels devido ao scale
    };
  }, []);

  // Função para converter coordenadas de canvas para coordenadas de tela
  const canvasToScreen = useCallback((canvasX, canvasY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: rect.left + canvasX,
      y: rect.top + canvasY
    };
  }, []);

  // Função para converter coordenadas de tela para coordenadas de canvas
  const screenToCanvas = useCallback((screenX, screenY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: screenX - rect.left,
      y: screenY - rect.top
    };
  }, []);

  // Função para limpar o canvas
  const clearCanvas = useCallback(() => {
    const ctx = contextRef.current;
    if (!ctx) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = devicePixelRatioRef.current;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
  }, []);

  // Função para desenhar retângulo com melhor qualidade
  const drawRect = useCallback((x, y, width, height, options = {}) => {
    const ctx = contextRef.current;
    if (!ctx) return;

    const {
      fillStyle,
      strokeStyle,
      lineWidth = 1,
      shadowColor,
      shadowBlur,
      shadowOffsetX = 0,
      shadowOffsetY = 0
    } = options;

    ctx.save();

    if (shadowColor) {
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur || 0;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = shadowOffsetY;
    }

    if (fillStyle) {
      ctx.fillStyle = fillStyle;
      ctx.fillRect(x, y, width, height);
    }

    if (strokeStyle) {
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.strokeRect(x, y, width, height);
    }

    ctx.restore();
  }, []);

  // Função para desenhar círculo com melhor qualidade
  const drawCircle = useCallback((x, y, radius, options = {}) => {
    const ctx = contextRef.current;
    if (!ctx) return;

    const {
      fillStyle,
      strokeStyle,
      lineWidth = 1,
      shadowColor,
      shadowBlur,
      shadowOffsetX = 0,
      shadowOffsetY = 0
    } = options;

    ctx.save();

    if (shadowColor) {
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur || 0;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = shadowOffsetY;
    }

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);

    if (fillStyle) {
      ctx.fillStyle = fillStyle;
      ctx.fill();
    }

    if (strokeStyle) {
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    }

    ctx.restore();
  }, []);

  // Função para desenhar linha com melhor qualidade
  const drawLine = useCallback((x1, y1, x2, y2, options = {}) => {
    const ctx = contextRef.current;
    if (!ctx) return;

    const {
      strokeStyle = '#000000',
      lineWidth = 1,
      lineCap = 'butt',
      lineJoin = 'miter',
      shadowColor,
      shadowBlur,
      shadowOffsetX = 0,
      shadowOffsetY = 0
    } = options;

    ctx.save();

    if (shadowColor) {
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur || 0;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = shadowOffsetY;
    }

    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = lineCap;
    ctx.lineJoin = lineJoin;

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    ctx.restore();
  }, []);

  // Função para desenhar texto com melhor qualidade
  const drawText = useCallback((text, x, y, options = {}) => {
    const ctx = contextRef.current;
    if (!ctx) return;

    const {
      fillStyle = '#000000',
      strokeStyle,
      lineWidth = 1,
      font = '12px Arial',
      textAlign = 'left',
      textBaseline = 'alphabetic',
      shadowColor,
      shadowBlur,
      shadowOffsetX = 0,
      shadowOffsetY = 0
    } = options;

    ctx.save();

    if (shadowColor) {
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur || 0;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = shadowOffsetY;
    }

    ctx.font = font;
    ctx.textAlign = textAlign;
    ctx.textBaseline = textBaseline;

    if (strokeStyle) {
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.strokeText(text, x, y);
    }

    if (fillStyle) {
      ctx.fillStyle = fillStyle;
      ctx.fillText(text, x, y);
    }

    ctx.restore();
  }, []);

  // Efeito para configurar o canvas inicial
  useEffect(() => {
    if (width && height) {
      setupCanvas(canvasRef.current, width, height);
    }
  }, [width, height, setupCanvas]);

  // Efeito para redimensionamento automático
  useEffect(() => {
    if (!autoResize) return;

    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const container = canvas.parentElement;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      resizeCanvas(containerWidth, containerHeight);
    };

    // Configurar resize inicial
    handleResize();

    // Adicionar listener de resize
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [autoResize, resizeCanvas]);

  return {
    canvasRef,
    context: contextRef.current,
    devicePixelRatio: devicePixelRatioRef.current,
    setupCanvas,
    resizeCanvas,
    getMousePosition,
    canvasToScreen,
    screenToCanvas,
    clearCanvas,
    drawRect,
    drawCircle,
    drawLine,
    drawText
  };
};

export default useCanvasScaling;

